import axios from "axios";
import toast from "react-hot-toast";

/** ──────────────────────────────────────────────────────────────────────────
 *  Functional Programming Utilities
 *  ────────────────────────────────────────────────────────────────────────── */

// Type definitions for functional composition
type CacheEntry<T> = { readonly v: T; readonly e: number };
type Result<T, E = Error> = { readonly success: true; readonly value: T } | { readonly success: false; readonly error: E };
type IO<T> = () => T;

// Pure function: Creates cache entry with TTL
const createCacheEntry = <T>(value: T, ttlMs: number): CacheEntry<T> => ({
  v: value,
  e: Date.now() + ttlMs,
});

// Pure function: Checks if cache entry is expired
const isCacheExpired = <T>(entry: CacheEntry<T>): boolean =>
  typeof entry.e === "number" && Date.now() > entry.e;

// Pure function: Parses JSON safely
const safeJsonParse = <T>(json: string): Result<T, string> => {
  try {
    return { success: true, value: JSON.parse(json) };
  } catch (error) {
    return { success: false, error: "Invalid JSON" };
  }
};

// Pure function: Serializes value to JSON safely
const safeJsonStringify = <T>(value: T): Result<string, string> => {
  try {
    return { success: true, value: JSON.stringify(value) };
  } catch (error) {
    return { success: false, error: "Serialization failed" };
  }
};

/** ──────────────────────────────────────────────────────────────────────────
 *  Local cache (localStorage) with 5-minute TTL - Functional approach
 *  ────────────────────────────────────────────────────────────────────────── */
const isBrowser = typeof window !== "undefined";
const LOCAL_TTL_MS = 5 * 60 * 1000; // 5 minutes

// IO operation: Set cache (side effect isolated)
const setLocalCache = <T>(key: string, value: T, ttlMs = LOCAL_TTL_MS): IO<Result<void, string>> => () => {
  if (!isBrowser) return { success: false, error: "Not in browser" };
  
  const entry = createCacheEntry(value, ttlMs);
  const serializedResult = safeJsonStringify(entry);
  
  if (!serializedResult.success) {
    return { success: false, error: serializedResult.error };
  }
  
  try {
    localStorage.setItem(key, serializedResult.value);
    return { success: true, value: undefined };
  } catch {
    return { success: false, error: "Storage write failed" };
  }
};

// IO operation: Get cache (side effect isolated)
const getLocalCache = <T>(key: string): IO<Result<T, string>> => () => {
  if (!isBrowser) return { success: false, error: "Not in browser" };
  
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { success: false, error: "Cache miss" };
    
    const parseResult = safeJsonParse<CacheEntry<T>>(raw);
    if (!parseResult.success) {
      return { success: false, error: parseResult.error };
    }
    
    const entry = parseResult.value;
    
    if (isCacheExpired(entry)) {
      localStorage.removeItem(key);
      return { success: false, error: "Cache expired" };
    }
    
    return { success: true, value: entry.v };
  } catch {
    return { success: false, error: "Cache read failed" };
  }
};

// IO operation: Clear cache
const clearLocalCache = (key: string): IO<Result<void, string>> => () => {
  if (!isBrowser) return { success: false, error: "Not in browser" };
  try {
    localStorage.removeItem(key);
    return { success: true, value: undefined };
  } catch {
    return { success: false, error: "Cache clear failed" };
  }
};

/** ──────────────────────────────────────────────────────────────────────────
 *  Types - Immutable and functional
 *  ────────────────────────────────────────────────────────────────────────── */
export type AgentRequestPayload = Readonly<{
  intent: string;
  entities: Readonly<{
    bucket: string;
    filename?: string;
    key?: string;
    content?: string;
    content_type?: string;
    data?: any;
  }>;
}>;

export type AgentResult<T = any> = Readonly<{
  data: T | null;
  statusCode: number;
  raw?: any;
}>;

export type PageResult<T = any> = Readonly<{
  data: T | null;
  error: any;
  isLoading: boolean;
  source: "cache" | "agent" | "none";
  statusCode: number;
  wasFallback: boolean;
}>;

/** ──────────────────────────────────────────────────────────────────────────
 *  Pure functions for HTTP configuration
 *  ────────────────────────────────────────────────────────────────────────── */

// Pure function: Creates axios config
const createHttpConfig = () => ({
  timeout: 15000,
  withCredentials: true,
  headers: { Accept: "application/json" },
});

// Pure function: Adds metadata to config
const addRequestMetadata = (config: any) => ({
  ...config,
  metadata: { start: Date.now() },
});

// Pure function: Calculates request duration
const calculateDuration = (startTime: number): number => Date.now() - startTime;

// Pure function: Checks if request is slow
const isSlowRequest = (durationMs: number): boolean => durationMs > 5000;

// Side effect: Logs slow request
const logSlowRequest = (durationMs: number, method: string | undefined, url: string | undefined): void => {
  console.warn(`Slow request (${durationMs}ms): ${method?.toUpperCase()} ${url}`);
};

// Side effect: Shows timeout toast
const showTimeoutToast = (): void => {
  toast("Request timed out", {
    duration: 4000,
    icon: "⏱️",
    style: { backgroundColor: "#001427", color: "#ffffff", border: "1px solid #9d7a1b" },
  });
};

// Pure function: Handles response with timing check
const handleResponseWithTiming = (response: any) => {
  const meta = response.config?.metadata;
  if (meta) {
    const duration = calculateDuration(meta.start);
    if (isSlowRequest(duration)) {
      logSlowRequest(duration, response.config.method, response.config.url);
    }
  }
  return response;
};

// Pure function: Handles request error
const handleRequestError = (error: any) => {
  if (error.code === "ECONNABORTED") {
    showTimeoutToast();
  }
  return Promise.reject(error);
};

// Axios instance with functional interceptors
const http = axios.create(createHttpConfig());

http.interceptors.request.use(addRequestMetadata);
http.interceptors.response.use(handleResponseWithTiming, handleRequestError);

/** ──────────────────────────────────────────────────────────────────────────
 *  Pure functions for data extraction and transformation
 *  ────────────────────────────────────────────────────────────────────────── */

// Pure function: Create agent payload for retrieving JSON
const createRetrievePayload = (page: string): AgentRequestPayload => ({
  intent: "do_retrieve_json_file",
  entities: {
    bucket: "mpb-site",
    filename: `${page}.json`,
  },
});

// Pure function: Create agent payload for updating JSON
const createUpdatePayload = (page: string, data: any): AgentRequestPayload => ({
  intent: "update_json_file",
  entities: {
    bucket: "mpb-site",
    filename: `${page}.json`,
    data,
  },
});

// Pure function: Extract page data from response body
const extractPageData = (body: any): any | null => {
  if (body?.result?.data && typeof body.result.data === "object") {
    return body.result.data;
  }
  if (body?.data && typeof body.data === "object" && !("success" in body && "bucket" in body)) {
    return body.data;
  }
  if (body?.result && typeof body.result === "object") {
    return body.result.data || body.result;
  }
  if (typeof body === "object") {
    return body;
  }
  return null;
};

// Pure function: Create result object
const createAgentResult = <T>(data: T | null, statusCode: number, raw?: any): AgentResult<T> => ({
  data,
  statusCode,
  raw,
});

// Pure function: Create page result
const createPageResult = <T>(
  data: T | null,
  error: any,
  isLoading: boolean,
  source: "cache" | "agent" | "none",
  statusCode: number,
  wasFallback: boolean
): PageResult<T> => ({
  data,
  error,
  isLoading,
  source,
  statusCode,
  wasFallback,
});

/** ──────────────────────────────────────────────────────────────────────────
 *  Functional MCP Agent calls
 *  ────────────────────────────────────────────────────────────────────────── */

// Pure async function: Get page from agent
export const getAgentPage = async (page: string): Promise<AgentResult> => {
  const payload = createRetrievePayload(page);
  
  try {
    const response = await http.post("/agent", payload, {
      headers: { "Content-Type": "application/json" },
    });

    const pageData = extractPageData(response.data);
    console.log(`Fetched page data for "${page}" from MCP agent.`);

    return createAgentResult(pageData, response.status, response.data);
  } catch (error: any) {
    if (error.response) {
      return createAgentResult(null, error.response.status, error.response.data);
    }
    throw error;
  }
};

// Pure async function: Upload image
export const uploadImageForAgent = async (
  payload: AgentRequestPayload
): Promise<AgentResult> => {
  try {
    const response = await http.post("/upload", payload);
    return createAgentResult(response.data ?? null, response.status, response.data);
  } catch (error: any) {
    console.error("Error uploading Image page data", error);
    if (error.response) {
      return createAgentResult(null, error.response.status, error.response.data);
    }
    throw error;
  }
};

// Pure async function: Update JSON
export const updateJsonForAgent = async (
  page: string,
  data: any
): Promise<AgentResult> => {
  const payload = createUpdatePayload(page, data);
  
  try {
    const response = await http.post("/agent", payload, {
      headers: { "Content-Type": "application/json" },
    });
    
    const pageData = extractPageData(response.data);
    return createAgentResult(pageData, response.status, response.data);
  } catch (error: any) {
    if (error.response) {
      return createAgentResult(null, error.response.status, error.response.data);
    }
    throw error;
  }
};

/** ──────────────────────────────────────────────────────────────────────────
 *  Functional composition for page fetching with cache
 *  ────────────────────────────────────────────────────────────────────────── */

// Pure function: Get page with caching strategy
export const getPage = async <T = any>(page: string): Promise<PageResult<T>> => {
  // Try cache first (pure IO operation)
  const cacheResult = getLocalCache<T>(page)();
  
  if (cacheResult.success) {
    return createPageResult(
      cacheResult.value,
      null,
      false,
      "cache",
      200,
      false
    );
  }

  // Cache miss - fetch from agent
  try {
    const agentResult = await getAgentPage(page);
    
    if (agentResult.data) {
      // Store in cache (side effect isolated in IO)
      setLocalCache(page, agentResult.data)();
      
      return createPageResult(
        agentResult.data,
        null,
        false,
        "agent",
        agentResult.statusCode,
        false
      );
    }
    
    return createPageResult(
      null,
      null,
      false,
      "none",
      agentResult.statusCode,
      false
    );
  } catch (error) {
    return createPageResult(
      null,
      error,
      false,
      "none",
      0,
      false
    );
  }
};

// Pure function: Manually seed/override cache
export const cachePageData = <T>(page: string, data: T): IO<Result<void, string>> =>
  setLocalCache(page, data);

// Pure function: Force-refresh by clearing cache
export const clearPageCache = (page: string): IO<Result<void, string>> =>
  clearLocalCache(page);

/** ──────────────────────────────────────────────────────────────────────────
 *  Higher-order functions for composition
 *  ────────────────────────────────────────────────────────────────────────── */

// HOF: Creates a cached version of any async function
export const withCache = <T, Args extends any[]>(
  fn: (...args: Args) => Promise<T>,
  keyGen: (...args: Args) => string,
  ttlMs = LOCAL_TTL_MS
) => async (...args: Args): Promise<T> => {
  const key = keyGen(...args);
  const cached = getLocalCache<T>(key)();
  
  if (cached.success) {
    return cached.value;
  }
  
  const result = await fn(...args);
  setLocalCache(key, result, ttlMs)();
  return result;
};

// HOF: Retry logic (functional)
export const withRetry = <T, Args extends any[]>(
  fn: (...args: Args) => Promise<T>,
  maxRetries = 3,
  delayMs = 1000
) => async (...args: Args): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn(...args);
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delayMs * (attempt + 1)));
      }
    }
  }
  
  throw lastError;
};

// Curried APIs for page-scoped operations
export const updateJsonForAgentCurried =
  (page: string) =>
    (data: any): Promise<AgentResult> =>
      updateJsonForAgent(page, data);

export const cachePageDataCurried =
  <T>(page: string) =>
    (data: T): IO<Result<void, string>> =>
      cachePageData(page, data);

// Fully functional app service facade
export const appService = {
  getPage,
  getAgentPage,
  updateJsonForAgent,
  uploadImageForAgent,
  cachePageData,
  clearPageCache,
  updateJsonForAgentCurried,
  cachePageDataCurried,
} as const;
